// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { createReference, getReferenceString, type MedplumClient } from '@medplum/core';
import type {
  Appointment,
  ClinicalImpression,
  Coding,
  Condition,
  DocumentReference,
  Encounter,
  Extension,
  MedicationRequest,
  MedicationStatement,
  Observation,
  Patient,
  Practitioner,
  Provenance,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  EncounterNoteAddendum,
  EncounterNoteConditionRow,
  EncounterNoteDiagnosisFormValue,
  EncounterNoteFormValues,
  EncounterNoteMedicationRow,
  FormCodeableConcept,
  NoteCode,
} from '../components/encounter/encounter-chart-types';
import { MIN_DIAGNOSIS_ENTRIES, NOTE_CODES } from '../components/encounter/encounter-chart-types';
import { writeReferenceChecklist } from '../components/encounter/encounter-note-checklist';
import { MEDICATION_FREQUENCY_OPTIONS } from '../constants/medical-info';
import { ChartNoteStatus } from '../types/encounter';
import { conditionRecordedDate, findExistingCondition } from '../utils/conditions';
import { concludeEncounterForLockedNote } from '../utils/encounter';
import { showErrorNotification } from '../utils/notifications';
import { getLatestHeightCm, getLatestWeightKg } from '../utils/patient-vitals';
import { attachPrescriptionDocumentToTask, createPrescriptionDocument } from '../utils/prescription-pdf';
import {
  attachReferralDocumentToTask,
  createReferralDocument,
  createServiceRequestReferralDocument,
  isReferralMedicationRequest,
  isReferralServiceRequest,
} from '../utils/referral-pdf';
import { withTenantScope } from '../utils/tenant';

const FHIR_ACT_REASON_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ActReason';
const FHIR_PROVENANCE_PARTICIPANT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/provenance-participant-type';
const FHIR_DOCUMENT_COMPLETION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion';
const PRINCIPAL_USE_CODING = [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'principal' }];
const SECONDARY_USE_CODING = [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'secondary' }];
const POSITIONAL_APNEA_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/suspected-positional-apnea';
const CONDITION_CATEGORY_SYSTEM = 'https://polarissleep.com/fhir/condition-category';
const NOTE_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/observation-type';
const CONFIRMATION_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/clinical-row-confirmation';
const SIGNED_NOTE_SNAPSHOT_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/signed-note-snapshot';
const ENCOUNTER_FREQUENCY_OPTIONS = new Set([
  'qd',
  'bid',
  'prn',
  'monthly',
  ...MEDICATION_FREQUENCY_OPTIONS.map((option) => option.value),
]);

const TASK_COMPLETED_STATUSES = new Set<Task['status']>([
  'completed',
  'cancelled',
  'failed',
  'rejected',
  'entered-in-error',
]);

function isGoneError(error: unknown): boolean {
  return error instanceof Error && error.message.toLowerCase().includes('gone');
}

let rowIdCounter = 0;

function createClientRowId(): string {
  rowIdCounter += 1;
  return `encounter-note-row-${rowIdCounter}`;
}

function isoToTimeString(iso: string | undefined): string {
  if (!iso) {
    return '';
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function mergeTimeToISO(timeStr: string, baseIso: string | undefined): string | undefined {
  if (!timeStr) {
    return baseIso;
  }

  const [hoursStr, minutesStr] = timeStr.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return baseIso;
  }

  const base = baseIso ? new Date(baseIso) : new Date();
  base.setHours(hours, minutes, 0, 0);
  return base.toISOString();
}

function getMedicationDosageText(statement: MedicationStatement): string {
  const dosage = statement.dosage?.[0];
  if (dosage?.text) {
    return dosage.text;
  }
  const quantity = dosage?.doseAndRate?.[0]?.doseQuantity;
  if (quantity?.value === undefined) {
    return '';
  }
  return [String(quantity.value), quantity.unit ?? quantity.code].filter(Boolean).join(' ');
}

function getMedicationFrequencyText(statement: MedicationStatement): string {
  const timing = statement.dosage?.[0]?.timing;
  const codedFrequency = timing?.code?.coding?.[0]?.code ?? timing?.code?.coding?.[0]?.display ?? timing?.code?.text;
  if (codedFrequency) {
    return codedFrequency;
  }
  const repeat = timing?.repeat;
  if (repeat?.frequency && repeat.period && repeat.periodUnit) {
    return `${repeat.frequency} time${repeat.frequency === 1 ? '' : 's'} every ${repeat.period} ${repeat.periodUnit}`;
  }
  return repeat?.when?.join(', ') ?? '';
}

function toDateInput(iso: string | undefined): string | null {
  if (!iso) {
    return null;
  }
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function buildDiagnosisFormValue(
  condition: Condition | undefined,
  id: string | undefined
): EncounterNoteDiagnosisFormValue {
  const code = condition?.code?.coding?.[0]?.code ?? '';
  const text = condition?.code?.text ?? condition?.code?.coding?.[0]?.display ?? '';
  return {
    id,
    text,
    code,
    concept: condition?.code,
    recordedDate: condition?.recordedDate,
  };
}

function sortEncounterDiagnoses(diagnoses: Encounter['diagnosis']): NonNullable<Encounter['diagnosis']> {
  return [...(diagnoses ?? [])].sort((left, right) => {
    const leftCode = left.use?.coding?.[0]?.code;
    const rightCode = right.use?.coding?.[0]?.code;
    if (leftCode === 'principal' && rightCode !== 'principal') {
      return -1;
    }
    if (rightCode === 'principal' && leftCode !== 'principal') {
      return 1;
    }
    return (left.rank ?? Number.MAX_SAFE_INTEGER) - (right.rank ?? Number.MAX_SAFE_INTEGER);
  });
}

function ensureMinimumDiagnoses(diagnoses: EncounterNoteDiagnosisFormValue[]): EncounterNoteDiagnosisFormValue[] {
  const nextDiagnoses = [...diagnoses];
  while (nextDiagnoses.length < MIN_DIAGNOSIS_ENTRIES) {
    nextDiagnoses.push({
      text: '',
      code: '',
    });
  }
  return nextDiagnoses;
}

function buildEncounterNoteValues(encounter: Encounter, appointment: Appointment | undefined): EncounterNoteFormValues {
  return {
    startTime: isoToTimeString(encounter.period?.start ?? appointment?.start),
    endTime: isoToTimeString(encounter.period?.end ?? appointment?.end),
    visitReason: encounter.reasonCode?.[0]?.text ?? '',
    assessment: '',
    hpi: '',
    treatmentPlan: '',
    vitalSigns: '',
    reviewSymptoms: '',
    exam: '',
    noteForPatient: '',
    noteForReferring: '',
    conditions: [],
    medications: [],
    diagnoses: ensureMinimumDiagnoses(
      sortEncounterDiagnoses(encounter.diagnosis).map((diagnosis) =>
        buildDiagnosisFormValue(undefined, diagnosis.condition?.reference?.split('/')[1])
      )
    ),
    suspectedPositionalApnea: encounter.extension?.find((e) => e.url === POSITIONAL_APNEA_EXTENSION)?.valueString ?? '',
  };
}

function buildCodeableConcept(
  text: string,
  code: string,
  concept: FormCodeableConcept | undefined
): FormCodeableConcept | undefined {
  if (concept?.coding?.length || concept?.text) {
    const display = text || concept.text || concept.coding?.[0]?.display || code;
    return {
      ...concept,
      text: display,
      coding:
        code || concept.coding?.[0]?.display || text
          ? [
              {
                ...concept.coding?.[0],
                code: code || concept.coding?.[0]?.code,
                display,
              },
            ]
          : concept.coding,
    };
  }

  if (!text && !code) {
    return undefined;
  }

  return {
    text: text || code,
    coding: code || text ? [{ code: code || undefined, display: text || code || undefined }] : undefined,
  };
}

function decodeAttachmentData(data: string | undefined): string {
  if (!data) {
    return '';
  }

  try {
    return atob(data);
  } catch {
    return '';
  }
}

export interface UseEncounterNoteControllerProps {
  encounter: Encounter;
  appointment: Appointment | undefined;
  patient: Patient;
  tasks: Task[];
  clinicalImpression: ClinicalImpression | undefined;
  setClinicalImpression: Dispatch<SetStateAction<ClinicalImpression | undefined>>;
  setEncounter: Dispatch<SetStateAction<Encounter | undefined>>;
  setTasks: Dispatch<SetStateAction<Task[]>>;
}

export interface EncounterNoteController {
  addendums: EncounterNoteAddendum[];
  chartNoteStatus: ChartNoteStatus;
  form: ReturnType<typeof useForm<EncounterNoteFormValues>>;
  isCompleted: boolean;
  isLocked: boolean;
  isSaving: boolean;
  medicationRequests: MedicationRequest[];
  provenances: Provenance[];
  refreshOrders: () => Promise<void>;
  saveDraft: (vitalSigns?: string, referenceChecklist?: boolean[]) => Promise<void>;
  serviceRequests: ServiceRequest[];
  tasks: Task[];
  sign: (practitioner: Reference<Practitioner>, lock: boolean) => Promise<void>;
  addAddendum: (text: string) => Promise<void>;
  toggleTask: (task: Task) => Promise<void>;
  confirmClinicalRow: (resourceType: 'MedicationStatement' | 'Condition', id: string) => Promise<void>;
}

/**
 * Applies a change to the server's current copy of a Task. The page's task list is loaded once and can be stale
 * (e.g. an order amended after page load rewrites its Task), so lock-time writes re-read the Task and write it back
 * with If-Match instead of writing the cached copy over newer changes.
 */
async function updateCurrentTask(medplum: MedplumClient, taskId: string, change: (task: Task) => Task): Promise<Task> {
  const current = await medplum.readResource('Task', taskId, { cache: 'no-cache' });
  return medplum.updateResource(
    withTenantScope(change(current)),
    current.meta?.versionId ? { headers: { 'If-Match': `W/"${current.meta.versionId}"` } } : undefined
  );
}

export function useEncounterNoteController(props: UseEncounterNoteControllerProps): EncounterNoteController {
  const { encounter, appointment, patient, tasks, clinicalImpression, setClinicalImpression, setEncounter, setTasks } =
    props;
  const medplum = useMedplum();
  const [provenances, setProvenances] = useState<Provenance[]>([]);
  const [addendums, setAddendums] = useState<EncounterNoteAddendum[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [medicationRequests, setMedicationRequests] = useState<MedicationRequest[]>([]);
  const [chartNoteStatus, setChartNoteStatus] = useState<ChartNoteStatus>(ChartNoteStatus.Unsigned);
  const [isSaving, setIsSaving] = useState(false);
  const noteObservationIdsRef = useRef<Map<NoteCode, string>>(new Map());
  const medicationIdsRef = useRef<Set<string>>(new Set());
  const conditionIdsRef = useRef<Set<string>>(new Set());
  const hydrationInFlightEncounterIdRef = useRef<string | undefined>(undefined);
  const hydratedEncounterIdRef = useRef<string | undefined>(undefined);

  const form = useForm<EncounterNoteFormValues>({
    initialValues: buildEncounterNoteValues(encounter, appointment),
  });
  const { resetDirty, setValues } = form;

  const isCompleted = encounter.status === 'finished';
  const isLocked = chartNoteStatus === ChartNoteStatus.SignedAndLocked;

  const refreshOrders = useCallback(async (): Promise<void> => {
    const [loadedServiceRequests, loadedMedicationRequests] = await Promise.all([
      medplum.searchResources('ServiceRequest', `encounter=Encounter/${encounter.id}`),
      medplum.searchResources('MedicationRequest', `encounter=Encounter/${encounter.id}`),
    ]);
    setServiceRequests(loadedServiceRequests);
    setMedicationRequests(loadedMedicationRequests);
  }, [encounter.id, medplum]);

  // Billing updates replace the Encounter object while the note is still being edited.
  // Keep the hydration snapshot stable so those updates do not overwrite unsaved form values.
  // biome-ignore lint/correctness/useExhaustiveDependencies: The encounter identity intentionally controls this snapshot.
  const encounterToHydrate = useMemo(() => encounter, [encounter.id]);

  useEffect(() => {
    const encounterId = encounterToHydrate.id;
    if (
      !encounterId ||
      hydrationInFlightEncounterIdRef.current === encounterId ||
      hydratedEncounterIdRef.current === encounterId
    ) {
      return;
    }

    // Mark hydration as started before the first await. Appointment, billing, and clinical-impression updates can
    // rerender this hook while the initial load is in flight; a second load would overwrite unsaved form values.
    hydrationInFlightEncounterIdRef.current = encounterId;
    let cancelled = false;

    const loadEncounterNoteData = async (): Promise<void> => {
      const encounterMedicationStatements = (await medplum.searchResources(
        'MedicationStatement',
        `patient=${getReferenceString(patient)}&context=Encounter/${encounterToHydrate.id}`
      )) as MedicationStatement[];
      const shouldHydratePatientData = !hydratedEncounterIdRef.current && !isLocked;
      const medicationStatements = shouldHydratePatientData
        ? (
            (await medplum.searchResources(
              'MedicationStatement',
              `patient=${getReferenceString(patient)}`
            )) as MedicationStatement[]
          ).filter((statement) => statement.status === 'active' && !statement.effectivePeriod?.end)
        : encounterMedicationStatements;
      const medicationRows: EncounterNoteMedicationRow[] = medicationStatements.map((medicationStatement) => {
        const frequency = getMedicationFrequencyText(medicationStatement);
        const rawMedication =
          medicationStatement.medicationCodeableConcept?.text ??
          medicationStatement.medicationCodeableConcept?.coding?.[0]?.display ??
          '';
        return {
          clientId: createClientRowId(),
          id: medicationStatement.id,
          medication: rawMedication,
          dosage: getMedicationDosageText(medicationStatement),
          frequency,
          frequencyOther: !!frequency && !ENCOUNTER_FREQUENCY_OPTIONS.has(frequency.toLowerCase()),
          dateStarted: toDateInput(medicationStatement.effectiveDateTime ?? medicationStatement.effectivePeriod?.start),
          dateStopped: toDateInput(medicationStatement.effectivePeriod?.end),
          isDateStoppedNA: !medicationStatement.effectivePeriod?.end,
          medicationConcept: medicationStatement.medicationCodeableConcept,
          sourceDate:
            medicationStatement.effectiveDateTime ??
            medicationStatement.effectivePeriod?.start ??
            medicationStatement.meta?.lastUpdated,
          confirmedAt: medicationStatement.extension?.find((extension) => extension.url === CONFIRMATION_EXTENSION)
            ?.valueDateTime,
          confirmedBy: medicationStatement.extension?.find((extension) => extension.url === CONFIRMATION_EXTENSION)
            ?.valueReference?.display,
        };
      });
      const medicationIdToClientId = new Map(
        medicationRows.filter((row) => row.id).map((row) => [row.id as string, row.clientId])
      );

      const diagnosisConditionIds = new Set(
        encounterToHydrate.diagnosis?.map((diagnosis) => diagnosis.condition?.reference?.split('/')[1]).filter(Boolean)
      );
      const conditionResults = shouldHydratePatientData
        ? ((await medplum.searchResources('Condition', `patient=${getReferenceString(patient)}`)) as Condition[])
        : ((await Promise.all(
            [...diagnosisConditionIds].map((id) =>
              medplum.readResource('Condition', id as string).catch(() => undefined)
            )
          )) as Array<Condition | undefined>);
      const conditionRows: EncounterNoteConditionRow[] = conditionResults
        .filter((condition): condition is Condition => {
          if (!condition?.id) {
            return false;
          }
          return (
            !diagnosisConditionIds.has(condition.id) &&
            condition.clinicalStatus?.coding?.[0]?.code !== 'inactive' &&
            condition.clinicalStatus?.coding?.[0]?.code !== 'resolved'
          );
        })
        .map((condition) => ({
          clientId: createClientRowId(),
          id: condition.id,
          condition: condition.code?.text ?? condition.code?.coding?.[0]?.display ?? '',
          code: condition.code?.coding?.[0]?.code ?? '',
          onsetDate: toDateInput(condition.onsetDateTime),
          medicationIds:
            condition.evidence?.flatMap((evidence) =>
              (evidence.detail ?? [])
                .map((detail) => detail.reference?.split('/')[1])
                .filter(Boolean)
                .map((id) => medicationIdToClientId.get(id as string) ?? (id as string))
            ) ?? [],
          concept: condition.code,
          categoryCode: condition.category?.[0]?.coding?.find((coding) => coding.system === CONDITION_CATEGORY_SYSTEM)
            ?.code as 'symptom' | 'comorbidity' | undefined,
          sourceDate: condition.recordedDate ?? condition.onsetDateTime ?? condition.meta?.lastUpdated,
          confirmedAt: condition.extension?.find((extension) => extension.url === CONFIRMATION_EXTENSION)
            ?.valueDateTime,
          confirmedBy: condition.extension?.find((extension) => extension.url === CONFIRMATION_EXTENSION)
            ?.valueReference?.display,
        }));

      const noteObservations = (await medplum.searchResources(
        'Observation',
        `encounter=Encounter/${encounterToHydrate.id}&code=${NOTE_CODES.map((code) => `${NOTE_OBSERVATION_SYSTEM}|${code}`).join(',')}`,
        { cache: 'no-cache' }
      )) as Observation[];
      const loadedNotes: Record<NoteCode, { id?: string; text: string }> = {
        assessment: { text: '' },
        hpi: { text: '' },
        'treatment-plan': { text: '' },
        'vital-signs': { text: '' },
        'review-symptoms': { text: '' },
        exam: { text: '' },
        'patient-note': { text: '' },
        'referring-note': { text: '' },
      };
      for (const observation of noteObservations) {
        const code = observation.code?.coding?.find((coding) => coding.system === NOTE_OBSERVATION_SYSTEM)?.code as
          | NoteCode
          | undefined;
        if (code) {
          loadedNotes[code] = {
            id: observation.id,
            text: observation.valueString ?? '',
          };
        }
      }

      const diagnosisValues = ensureMinimumDiagnoses(
        sortEncounterDiagnoses(encounterToHydrate.diagnosis).map((diagnosis) =>
          buildDiagnosisFormValue(undefined, diagnosis.condition?.reference?.split('/')[1])
        )
      );
      const [diagnosisConditions, loadedProvenances, loadedAddendums] = await Promise.all([
        Promise.all(
          diagnosisValues.map((diagnosis) =>
            diagnosis.id
              ? medplum.readResource('Condition', diagnosis.id).catch(() => undefined)
              : Promise.resolve(undefined)
          )
        ),
        medplum.searchResources('Provenance', `target=${getReferenceString(encounterToHydrate)}`),
        medplum.searchResources('DocumentReference', {
          encounter: `Encounter/${encounterToHydrate.id}`,
          type: '55107-7',
          _sort: '-date',
        }),
      ]);
      const typedDiagnosisConditions = diagnosisConditions as Array<Condition | undefined>;
      const typedLoadedProvenances = loadedProvenances as Provenance[];
      const typedLoadedAddendums = loadedAddendums as DocumentReference[];

      await refreshOrders();

      if (cancelled) {
        return;
      }

      medicationIdsRef.current = new Set(medicationRows.filter((row) => row.id).map((row) => row.id as string));
      conditionIdsRef.current = new Set(conditionRows.filter((row) => row.id).map((row) => row.id as string));
      for (const code of NOTE_CODES) {
        const observationId = loadedNotes[code].id;
        if (observationId) {
          noteObservationIdsRef.current.set(code, observationId);
        }
      }

      const nextValues: EncounterNoteFormValues = {
        ...buildEncounterNoteValues(encounterToHydrate, appointment),
        medications: medicationRows,
        conditions: conditionRows,
        diagnoses: ensureMinimumDiagnoses(
          diagnosisValues.map((diagnosis, index) =>
            buildDiagnosisFormValue(typedDiagnosisConditions[index], diagnosis.id)
          )
        ),
        assessment: loadedNotes['assessment'].text,
        hpi: loadedNotes.hpi.text,
        treatmentPlan: loadedNotes['treatment-plan'].text,
        vitalSigns: loadedNotes['vital-signs'].text,
        reviewSymptoms: loadedNotes['review-symptoms'].text,
        exam: loadedNotes.exam.text,
        noteForPatient: loadedNotes['patient-note'].text,
        noteForReferring: loadedNotes['referring-note'].text,
      };
      setValues(nextValues);
      resetDirty(nextValues);
      setProvenances(typedLoadedProvenances);
      setAddendums(
        typedLoadedAddendums.map((documentReference) => ({
          id: documentReference.id,
          authorName: documentReference.author?.[0]?.display || 'Unknown Author',
          timestamp: documentReference.date ?? '',
          text: decodeAttachmentData(documentReference.content?.[0]?.attachment?.data),
        }))
      );
      if (loadedProvenances.length > 0 && clinicalImpression?.status === 'completed') {
        setChartNoteStatus(ChartNoteStatus.SignedAndLocked);
      } else if (loadedProvenances.length > 0) {
        setChartNoteStatus(ChartNoteStatus.Signed);
      } else {
        setChartNoteStatus((currentStatus) =>
          currentStatus === ChartNoteStatus.SignedAndLocked ? currentStatus : ChartNoteStatus.Unsigned
        );
      }
      if (shouldHydratePatientData) {
        const [heightCm, weightKg] = await Promise.all([
          getLatestHeightCm(medplum, patient.id as string),
          getLatestWeightKg(medplum, patient.id as string),
        ]);
        if (!loadedNotes['vital-signs'].text && (heightCm !== undefined || weightKg !== undefined)) {
          const vitalText = [
            heightCm !== undefined ? `Height: ${heightCm} cm` : '',
            weightKg !== undefined ? `Weight: ${weightKg} kg` : '',
          ]
            .filter(Boolean)
            .join('\n');
          setValues((current) => ({ ...current, vitalSigns: vitalText }));
        }
      }
      hydratedEncounterIdRef.current = encounterId;
    };

    loadEncounterNoteData()
      .catch(showErrorNotification)
      .finally(() => {
        if (hydrationInFlightEncounterIdRef.current === encounterId && hydratedEncounterIdRef.current !== encounterId) {
          hydrationInFlightEncounterIdRef.current = undefined;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    appointment,
    clinicalImpression?.status,
    encounterToHydrate,
    isLocked,
    medplum,
    patient,
    refreshOrders,
    resetDirty,
    setValues,
  ]);

  const saveDraft = useCallback(
    async (vitalSigns?: string, referenceChecklist?: boolean[]): Promise<void> => {
      const values = vitalSigns === undefined ? form.values : { ...form.values, vitalSigns };
      if (values.medications.some((row) => row.frequencyOther && !row.frequency.trim())) {
        notifications.show({ color: 'red', message: 'Enter a frequency for Other' });
        return;
      }
      setIsSaving(true);
      try {
        const upsertDiagnosisCondition = async (
          value: EncounterNoteDiagnosisFormValue
        ): Promise<string | undefined> => {
          const concept = buildCodeableConcept(value.text, value.code, value.concept);
          if (!concept) {
            return undefined;
          }

          const resource: Condition = withTenantScope({
            resourceType: 'Condition',
            subject: { reference: getReferenceString(patient) },
            encounter: { reference: `Encounter/${encounter.id}` },
            code: concept,
            recordedDate: value.recordedDate ?? dayjs().format('YYYY-MM-DD'),
          } as Condition);

          if (value.id) {
            const existing = await medplum.readResource('Condition', value.id).catch(() => undefined);
            if (existing) {
              const typedExisting = existing as Condition;
              const updated = await medplum.updateResource<Condition>({
                ...typedExisting,
                ...resource,
                id: value.id,
                recordedDate: value.recordedDate ?? typedExisting.recordedDate ?? conditionRecordedDate(typedExisting),
              });
              return updated.id ? `Condition/${updated.id}` : undefined;
            }
          }

          const existing = await findExistingCondition(medplum, patient, concept);
          if (existing?.id) {
            const updated = await medplum.updateResource<Condition>({
              ...existing,
              code: concept,
              id: existing.id,
            });
            return updated.id ? `Condition/${updated.id}` : undefined;
          }

          const created = await medplum.createResource(resource);
          return created.id ? `Condition/${created.id}` : undefined;
        };

        const diagnosisReferences = await Promise.all(
          values.diagnoses.map((diagnosis) => upsertDiagnosisCondition(diagnosis))
        );
        const savedDiagnoses = values.diagnoses.map((diagnosis, index) => ({
          ...diagnosis,
          id: diagnosisReferences[index]?.split('/')[1],
          concept: buildCodeableConcept(diagnosis.text, diagnosis.code, diagnosis.concept),
        }));
        const encounterDiagnoses = diagnosisReferences.reduce<NonNullable<Encounter['diagnosis']>>((acc, reference) => {
          if (!reference) {
            return acc;
          }

          acc.push({
            condition: { reference },
            rank: acc.length + 1,
            use: { coding: acc.length === 0 ? PRINCIPAL_USE_CODING : SECONDARY_USE_CODING },
          });
          return acc;
        }, []);

        const filteredExtensions = (encounter.extension ?? []).filter(
          (extension) => extension.url !== POSITIONAL_APNEA_EXTENSION
        );
        const extensionsWithPositionalApnea = values.suspectedPositionalApnea
          ? [...filteredExtensions, { url: POSITIONAL_APNEA_EXTENSION, valueString: values.suspectedPositionalApnea }]
          : filteredExtensions;
        const encounterExtensions =
          referenceChecklist === undefined
            ? extensionsWithPositionalApnea
            : writeReferenceChecklist(extensionsWithPositionalApnea, referenceChecklist);
        const updatedEncounter: Encounter = {
          ...encounter,
          period: {
            ...encounter.period,
            start: mergeTimeToISO(values.startTime, encounter.period?.start ?? appointment?.start),
            end: mergeTimeToISO(values.endTime, encounter.period?.end ?? appointment?.end),
          },
          reasonCode: values.visitReason ? [{ text: values.visitReason }] : [],
          diagnosis: encounterDiagnoses,
          extension: encounterExtensions,
        };
        const savedEncounter = await medplum.updateResource(updatedEncounter);
        setEncounter(savedEncounter);

        const noteEntries: Array<{ code: NoteCode; text: string }> = [
          { code: 'assessment', text: values.assessment },
          { code: 'hpi', text: values.hpi },
          { code: 'treatment-plan', text: values.treatmentPlan },
          { code: 'vital-signs', text: values.vitalSigns },
          { code: 'review-symptoms', text: values.reviewSymptoms },
          { code: 'exam', text: values.exam },
          { code: 'patient-note', text: values.noteForPatient },
          { code: 'referring-note', text: values.noteForReferring },
        ];
        await Promise.all(
          noteEntries.map(async ({ code, text }) => {
            const existingId = noteObservationIdsRef.current.get(code);
            const hasText = text.trim().length > 0;
            if (!hasText && !existingId) {
              return;
            }

            const baseObservation: Observation = withTenantScope({
              resourceType: 'Observation',
              status: 'final',
              subject: { reference: getReferenceString(patient) },
              encounter: { reference: `Encounter/${encounter.id}` },
              code: { coding: [{ system: NOTE_OBSERVATION_SYSTEM, code }] },
              ...(hasText ? { valueString: text } : {}),
            } as Observation);

            if (existingId) {
              await medplum.updateResource<Observation>({ ...baseObservation, id: existingId });
              return;
            }

            const created = await medplum.createResource<Observation>(baseObservation);
            if (created.id) {
              noteObservationIdsRef.current.set(code, created.id);
            }
          })
        );

        const currentMedicationIds = new Set(values.medications.filter((row) => row.id).map((row) => row.id as string));
        const medicationIdsToStop = [...medicationIdsRef.current].filter((id) => !currentMedicationIds.has(id));
        await Promise.all(
          medicationIdsToStop.map(async (id) => {
            const existing = (await medplum.readResource('MedicationStatement', id)) as MedicationStatement;
            await medplum.updateResource<MedicationStatement>({
              ...existing,
              status: 'completed',
              effectivePeriod: { ...existing.effectivePeriod, end: new Date().toISOString() },
            });
          })
        );

        const savedMedicationRows = await Promise.all(
          values.medications.map(async (row): Promise<EncounterNoteMedicationRow> => {
            const concept = buildCodeableConcept(row.medication, '', row.medicationConcept);
            if (!concept && !row.dosage && !row.frequency && !row.dateStarted && !row.dateStopped) {
              return row;
            }

            const resource: MedicationStatement = withTenantScope({
              resourceType: 'MedicationStatement',
              status: row.dateStopped ? 'completed' : 'active',
              subject: { reference: getReferenceString(patient) },
              ...(row.id ? {} : { context: { reference: `Encounter/${encounter.id}` } }),
              medicationCodeableConcept: concept ?? { text: row.medication },
              effectivePeriod:
                row.dateStarted || row.dateStopped
                  ? {
                      ...(row.dateStarted ? { start: dayjs(row.dateStarted).toISOString() } : {}),
                      ...(row.dateStopped ? { end: dayjs(row.dateStopped).toISOString() } : {}),
                    }
                  : undefined,
              dosage:
                row.dosage || row.frequency
                  ? [
                      {
                        text: row.dosage,
                        timing: row.frequency ? { code: { text: row.frequency } } : undefined,
                      },
                    ]
                  : undefined,
            });

            if (row.id) {
              const existing = (await medplum.readResource('MedicationStatement', row.id)) as MedicationStatement;
              const updated = await medplum.updateResource<MedicationStatement>({
                ...existing,
                ...resource,
                id: row.id,
              });
              if (updated.id) {
                medicationIdsRef.current.add(updated.id);
              }
              return { ...row, id: updated.id };
            }

            const created = await medplum.createResource<MedicationStatement>(resource);
            if (created.id) {
              medicationIdsRef.current.add(created.id);
            }
            return { ...row, id: created.id };
          })
        );

        const medicationClientToId = new Map(
          savedMedicationRows.filter((row) => row.id).map((row) => [row.clientId, row.id as string])
        );

        const currentConditionIds = new Set(values.conditions.filter((row) => row.id).map((row) => row.id as string));
        const conditionIdsToResolve = [...conditionIdsRef.current].filter((id) => !currentConditionIds.has(id));
        await Promise.all(
          conditionIdsToResolve.map(async (id) => {
            const existing = (await medplum.readResource('Condition', id)) as Condition;
            await medplum.updateResource<Condition>({
              ...existing,
              clinicalStatus: {
                coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'resolved' }],
              },
            });
          })
        );

        const savedConditionRows = await Promise.all(
          values.conditions.map(async (row): Promise<EncounterNoteConditionRow> => {
            const concept = buildCodeableConcept(row.condition, row.code, row.concept);
            if (!concept && row.medicationIds.length === 0 && !row.onsetDate) {
              return row;
            }

            const categoryCode =
              row.categoryCode ??
              (concept?.coding?.[0]?.system?.toLowerCase().includes('snomed') ? 'symptom' : 'comorbidity');

            const resource: Condition = withTenantScope({
              resourceType: 'Condition',
              subject: { reference: getReferenceString(patient) },
              ...(row.id ? {} : { encounter: { reference: `Encounter/${encounter.id}` } }),
              category: [{ coding: [{ system: CONDITION_CATEGORY_SYSTEM, code: categoryCode }] }],
              code: concept ?? { text: row.condition },
              ...(row.onsetDate ? { onsetDateTime: dayjs(row.onsetDate).toISOString() } : {}),
              ...(row.medicationIds.length > 0
                ? {
                    evidence: [
                      {
                        detail: row.medicationIds
                          .map((clientId) => medicationClientToId.get(clientId))
                          .filter(Boolean)
                          .map((id) => ({ reference: `MedicationStatement/${id}` })),
                      },
                    ],
                  }
                : {}),
            } as Condition);

            if (row.id) {
              const existing = await medplum.readResource('Condition', row.id);
              const updated = await medplum.updateResource<Condition>({ ...existing, ...resource, id: row.id });
              if (updated.id) {
                conditionIdsRef.current.add(updated.id);
              }
              return { ...row, id: updated.id, categoryCode };
            }

            const created = await medplum.createResource<Condition>(resource);
            if (created.id) {
              conditionIdsRef.current.add(created.id);
            }
            return { ...row, id: created.id, categoryCode };
          })
        );

        const nextValues: EncounterNoteFormValues = {
          ...values,
          medications: savedMedicationRows,
          conditions: savedConditionRows,
          diagnoses: ensureMinimumDiagnoses(savedDiagnoses),
        };
        form.setValues(nextValues);
        form.resetDirty(nextValues);
        notifications.show({ color: 'green', message: 'Chart saved' });
      } catch (error) {
        showErrorNotification(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [appointment, encounter, form, medplum, patient, setEncounter]
  );

  const sign = useCallback(
    async (practitioner: Reference<Practitioner>, lock: boolean): Promise<void> => {
      if (lock) {
        if (clinicalImpression) {
          if (clinicalImpression.status !== 'completed') {
            const updatedClinicalImpression = await medplum.updateResource<ClinicalImpression>({
              ...clinicalImpression,
              status: 'completed',
            });
            setClinicalImpression(updatedClinicalImpression);
          }
        } else {
          const createdClinicalImpression = await medplum.createResource<ClinicalImpression>(
            withTenantScope({
              resourceType: 'ClinicalImpression',
              status: 'completed',
              description: 'Encounter note locked',
              subject: { reference: getReferenceString(patient) },
              encounter: { reference: `Encounter/${encounter.id}` },
              date: new Date().toISOString(),
            })
          );
          setClinicalImpression(createdClinicalImpression);
        }
      }

      if (lock) {
        const tasksToUpdate = tasks.filter((task) => !TASK_COMPLETED_STATUSES.has(task.status));
        const updatedTasks = await Promise.all(
          tasksToUpdate.map(async (task) => {
            try {
              // Which tasks are completed is unchanged; the write uses the server's current copy so it cannot undo
              // changes made since page load (e.g. an amended DME order rewriting its task).
              return await updateCurrentTask(medplum, task.id as string, (current) => ({
                ...current,
                status: 'completed' as const,
              }));
            } catch (error) {
              if (isGoneError(error)) {
                return undefined;
              }
              throw error;
            }
          })
        );
        setTasks(
          tasks.map((task) => {
            const updatedTask = updatedTasks.find((candidate) => candidate?.id === task.id);
            return updatedTask || task;
          })
        );
      }

      const signedSnapshot = JSON.stringify({ vitalSigns: form.values.vitalSigns });
      const snapshotEncounter: Encounter = {
        ...encounter,
        extension: [
          ...(encounter.extension ?? []).filter((extension) => extension.url !== SIGNED_NOTE_SNAPSHOT_EXTENSION),
          { url: SIGNED_NOTE_SNAPSHOT_EXTENSION, valueString: signedSnapshot },
        ],
      };
      const snapshotSavedEncounter = await medplum.updateResource(snapshotEncounter);
      setEncounter(snapshotSavedEncounter);

      const newProvenance = await medplum.createResource<Provenance>(
        withTenantScope({
          resourceType: 'Provenance',
          target: [createReference(encounter)],
          recorded: new Date().toISOString(),
          reason: [
            {
              coding: [
                {
                  system: FHIR_ACT_REASON_SYSTEM,
                  code: 'SIGN',
                  display: 'Signed',
                },
              ],
            },
          ],
          agent: [
            {
              type: {
                coding: [
                  {
                    system: FHIR_PROVENANCE_PARTICIPANT_TYPE_SYSTEM,
                    code: 'author',
                  },
                ],
              },
              who: practitioner,
            },
          ],
          signature: [
            {
              type: [
                {
                  system: FHIR_DOCUMENT_COMPLETION_SYSTEM,
                  code: 'LA',
                  display: 'legally authenticated',
                },
              ],
              when: new Date().toISOString(),
              who: practitioner,
            },
          ],
        })
      );

      const signingPractitioner = await medplum.readReference(practitioner);
      await Promise.all(
        medicationRequests
          .filter((request) => request.id && request.status === 'active')
          .map(async (request) => {
            if (!lock && isReferralMedicationRequest(request) && request.encounter?.reference) {
              return;
            }
            // Only current documents count: a PDF superseded by an amendment (e.g. a prescription amended into a
            // referral) must not stop the amended order's form from being generated.
            const relatedDocuments = (await medplum.searchResources('DocumentReference', {
              related: `MedicationRequest/${request.id}`,
              status: 'current',
              _count: '10',
            })) as DocumentReference[];
            const existingDocument = relatedDocuments.find((candidate) =>
              isReferralMedicationRequest(request)
                ? candidate.type?.coding?.some((coding) => coding.code === 'referral-form')
                : !candidate.type?.coding?.some((coding) => coding.code === 'referral-form')
            );
            const document =
              existingDocument ??
              (isReferralMedicationRequest(request)
                ? await createReferralDocument(medplum, patient, signingPractitioner, request)
                : await createPrescriptionDocument(medplum, patient, signingPractitioner, request));
            const relatedTask = tasks.find((task) => task.focus?.reference === `MedicationRequest/${request.id}`);
            if (relatedTask?.id && !existingDocument) {
              await updateCurrentTask(medplum, relatedTask.id, (task) =>
                isReferralMedicationRequest(request)
                  ? attachReferralDocumentToTask(task, document)
                  : attachPrescriptionDocumentToTask(task, document)
              );
            }
          })
      );
      await Promise.all(
        serviceRequests
          .filter((request) => request.id && isReferralServiceRequest(request))
          .map(async (request) => {
            if (!lock || !request.id) {
              return;
            }
            const relatedDocuments = await medplum.searchResources('DocumentReference', {
              related: `ServiceRequest/${request.id}`,
              _count: '10',
            });
            const existingDocument = relatedDocuments.find((candidate) =>
              candidate.type?.coding?.some((coding: Coding) => coding.code === 'referral-form')
            );
            const document =
              existingDocument ??
              (await createServiceRequestReferralDocument(medplum, patient, signingPractitioner, request));
            const relatedTask = tasks.find((task) => task.focus?.reference === `ServiceRequest/${request.id}`);
            if (relatedTask?.id && !existingDocument) {
              await updateCurrentTask(medplum, relatedTask.id, (task) => attachReferralDocumentToTask(task, document));
            }
          })
      );

      setProvenances((current) => [...current, newProvenance]);
      setChartNoteStatus(lock ? ChartNoteStatus.SignedAndLocked : ChartNoteStatus.Signed);

      // Locking legally authenticates the record, so the visit is by definition
      // over — conclude it as successful rather than leaving the Appointment
      // un-fulfilled and the claim unsubmitted. Runs after the Provenance so a
      // failure here can't cost the signature; the Conclude button stays
      // available as a manual fallback.
      if (lock) {
        const concluded = await concludeEncounterForLockedNote(medplum, encounter, appointment);
        if (concluded) {
          setEncounter(concluded);
        }
      }
    },
    [
      appointment,
      clinicalImpression,
      encounter,
      form.values.vitalSigns,
      medplum,
      patient,
      setClinicalImpression,
      setEncounter,
      setTasks,
      tasks,
      medicationRequests,
      serviceRequests,
    ]
  );

  const addAddendum = useCallback(
    async (text: string): Promise<void> => {
      const author = medplum.getProfile();
      const authorReference = author ? createReference(author) : undefined;
      const created = await medplum.createResource<DocumentReference>(
        withTenantScope({
          resourceType: 'DocumentReference',
          status: 'current',
          type: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '55107-7',
                display: 'Addendum Document',
              },
            ],
          },
          subject: encounter.subject,
          author: authorReference
            ? [
                {
                  reference: authorReference.reference,
                  display: authorReference.display,
                },
              ]
            : undefined,
          context: {
            encounter: [
              {
                reference: `Encounter/${encounter.id}`,
              },
            ],
          },
          date: new Date().toISOString(),
          content: [
            {
              attachment: {
                contentType: 'text/plain',
                data: btoa(text),
                title: 'Addendum',
              },
            },
          ],
        })
      );

      setAddendums((current) => [
        {
          id: created.id,
          authorName: authorReference?.display || 'Unknown Author',
          timestamp: created.date ?? new Date().toISOString(),
          text,
        },
        ...current,
      ]);
    },
    [encounter.id, encounter.subject, medplum]
  );

  const confirmClinicalRow = useCallback(
    async (resourceType: 'MedicationStatement' | 'Condition', id: string): Promise<void> => {
      const resource = await medplum.readResource(resourceType, id);
      const profile = medplum.getProfile();
      const extension = {
        url: CONFIRMATION_EXTENSION,
        valueDateTime: new Date().toISOString(),
        valueReference: profile
          ? { reference: `${profile.resourceType}/${profile.id}`, display: profile.name?.[0]?.text }
          : undefined,
      };
      await medplum.updateResource({
        ...resource,
        extension: [
          ...(resource.extension ?? []).filter((current: Extension) => current.url !== CONFIRMATION_EXTENSION),
          extension,
        ],
      });
    },
    [medplum]
  );

  const toggleTask = useCallback(
    async (task: Task): Promise<void> => {
      const newStatus: Task['status'] = TASK_COMPLETED_STATUSES.has(task.status) ? 'in-progress' : 'completed';
      const updatedTask = await medplum.updateResource({ ...task, status: newStatus });
      setTasks(tasks.map((currentTask) => (currentTask.id === task.id ? updatedTask : currentTask)));
    },
    [medplum, setTasks, tasks]
  );

  return useMemo(
    () => ({
      addAddendum,
      confirmClinicalRow,
      addendums,
      chartNoteStatus,
      form,
      isCompleted,
      isLocked,
      isSaving,
      medicationRequests,
      provenances,
      refreshOrders,
      saveDraft,
      serviceRequests,
      sign,
      tasks,
      toggleTask,
    }),
    [
      addAddendum,
      confirmClinicalRow,
      addendums,
      chartNoteStatus,
      form,
      isCompleted,
      isLocked,
      isSaving,
      medicationRequests,
      provenances,
      refreshOrders,
      saveDraft,
      serviceRequests,
      sign,
      tasks,
      toggleTask,
    ]
  );
}
